import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";

import * as url from "helper/UrlHelper";
import { postData } from "utils/Gateway";

const CsvCustomUploadModal = ({ afterModalClose = () => {} }) => {
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");

  const taskFields = {
    title: { required: true, label: "Task Title" },
    description: { required: false, label: "Description" },
    project: { required: false, label: "Project (exact title)" },
    startdate: { required: false, label: "Start Date (yyyy-mm-dd)" },
    enddate: { required: false, label: "End Date (yyyy-mm-dd)" },
    hours: { required: false, label: "Planned Hours" },
    priority: { required: false, label: "Priority (1/2/3)" },
    assigneduser: { required: false, label: "Assigned User Name" },
    assigneduseremail: { required: false, label: "Assigned User Email (Assigned user email)" },
    followers: { required: false, label: "Followers (comma-separated emails)" },
  };

  const [fileData, setFileData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [validationError, setValidationError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    created: 0,
    failed: 0,
    totalProcessed: 0,
    records: [],
  });

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadStats({ created: 0, failed: 0, totalProcessed: 0, records: [] });
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          header: 1,
          raw: true,
        });
        if (sheet.length > 1) {
          const cleanHeaders = sheet[0]
            .map((header) => (header ? header.toString().trim() : ""))
            .filter(Boolean);

          // helper to convert Excel serial date to yyyy-mm-dd
          const excelSerialToYMD = (serial) => {
            try {
              const utcDays = serial - 25569; // Excel -> Unix epoch days
              const ms = Math.round(utcDays * 86400 * 1000);
              const d = new Date(ms);
              if (isNaN(d.getTime())) return serial;
              return d.toISOString().split("T")[0];
            } catch (e) {
              return serial;
            }
          };

          // Build index map from cleanHeaders to original sheet column indexes
          const headerIndexMap = [];
          sheet[0].forEach((h, idx) => {
            const hh = h ? h.toString().trim() : "";
            if (hh) headerIndexMap.push(idx);
          });

          // detect date-like header columns (header contains 'date' or equals start/end)
          const dateColPositions = cleanHeaders
            .map((h, idx) => ({ h, pos: idx }))
            .filter(({ h }) => h && /date|start|end/i.test(h))
            .map(({ pos }) => pos);

          const rawRows = sheet.slice(1).filter((row) => row.some((cell) => cell));

          // normalize rows: align to cleanHeaders and convert Date objects or Excel serial numbers for date columns
          const normalizedRows = rawRows.map((row) =>
            headerIndexMap.map((origIdx, newIdx) => {
              const cell = row[origIdx];
              if (cell instanceof Date) return cell.toISOString().split("T")[0];
              if (dateColPositions.includes(newIdx) && typeof cell === "number") {
                return excelSerialToYMD(cell);
              }
              return cell;
            }),
          );

          setHeaders(cleanHeaders);
          setFileData(normalizedRows);
          setValidationError("");
        } else {
          setValidationError("The file appears to be empty or invalid.");
        }
      } catch (error) {
        setValidationError(
          "Error reading file. Please ensure it's a valid Excel or CSV file."
        );
        console.error("File reading error:", error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleMappingChange = (field, headerValue) => {
    setMapping((prev) => ({ ...prev, [field]: headerValue || undefined }));
  };

  const validateMapping = () => {
    const requiredFields = Object.keys(taskFields).filter(
      (field) => taskFields[field].required
    );
    const unmappedRequired = requiredFields.filter((field) => !mapping[field]);
    if (unmappedRequired.length > 0) {
      setInvalidFields(unmappedRequired);
      setValidationError(
        `Please map these required fields: ${unmappedRequired
          .map((f) => taskFields[f].label)
          .join(", ")}`
      );
      return false;
    }
    setInvalidFields([]);
    setValidationError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateMapping()) return;
    setIsLoading(true);

    if (fileData && Object.keys(mapping).length > 0) {
      const batchSize = 50;
      const totalBatches = Math.ceil(fileData.length / batchSize);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const batchData = fileData.slice(
          batchIndex * batchSize,
          (batchIndex + 1) * batchSize
        );

        const payload = {
          mapping,
          header: headers,
          filedata: batchData,
        };

        try {
          let requestUrl = `${url.API_BASE_URL}${url.API_SAVE_TASKS_MASSIVEUPLOAD}`;
          const response = await postData(requestUrl, payload);

          if (response.status) {
            setUploadStats((prev) => ({
              created: prev.created + (response.data.created || 0),
              failed: prev.failed + (response.data.failed || 0),
              totalProcessed: prev.totalProcessed + batchData.length,
              records: prev.records.concat(response.data.records || []),
            }));
          } else {
            setUploadStats((prev) => ({
              ...prev,
              failed: prev.failed + batchData.length,
            }));
          }
        } catch (error) {
          console.error("Error processing batch", batchIndex + 1, ":", error);
          setUploadStats((prev) => ({
            ...prev,
            failed: prev.failed + batchData.length,
          }));
        }
      }
    }

    setIsLoading(false);
  };

  const handleCloseModal = () => {
    setFileData(null);
    setHeaders([]);
    setMapping({});
    setValidationError("");
    setInvalidFields([]);
    setUploadStats({ created: 0, failed: 0, totalProcessed: 0, records: [] });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getSampleValue = (field) => {
    if (!mapping[field] || !fileData || fileData.length === 0) return "";
    const headerIndex = headers.indexOf(mapping[field]);
    if (headerIndex === -1) return "";
    for (let row of fileData) {
      if (row[headerIndex] && row[headerIndex].toString().trim())
        return row[headerIndex].toString().trim();
    }
    return "";
  };

  return (
    <div
      className="modal fade"
      id="csvUploadModal"
      tabIndex="-1"
      aria-labelledby="csvUploadModalLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div className="modal-dialog modal-xl" style={{ maxWidth: "90vw" }}>
        <div className="modal-content bg-white border-0 rounded-15 shadow-lg">
          <div className="modal-header bg-primary text-white px-4 py-3 border-bottom border-gray-300">
            <div className="d-flex align-items-center gap-3">
              <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
                <span className="material-symbols-outlined text-primary fs-5 icon-md">
                  upload_file
                </span>
              </div>
              <div>
                <h3 className="fw-bold fs-2 text-white mb-0" id="csvUploadModalLabel">
                  Upload Tasks from Excel/CSV
                </h3>
                <p className="text-white fs-sm mb-0">
                  Upload multiple tasks at once using this feature.
                </p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleCloseModal}
            ></button>
          </div>

          <div className="modal-body p-4">
            <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
              {isLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
                  style={{ zIndex: 1050 }}
                >
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-primary fw-semibold mb-0">Uploading tasks, please wait...</p>
                </div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="fileInput" className="form-label fs-md fw-semibold">Choose File</label>
                  <input
                    type="file"
                    className="form-control bg-white shadow-none border-gray-300"
                    id="fileInput"
                    ref={fileInputRef}
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileUpload}
                  />
                </div>
                <div className="col-md-6">
                  {fileData && (
                    <>
                      <label className="form-label fs-md fw-semibold">File Loaded Successfully</label>
                      <div className="text text-primary d-flex align-items-center fw-semibold">
                        <i className="bi bi-check-circle me-2"></i>
                        Rows Found to be uploaded: {fileData.length}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {headers.length > 0 && (
                <div className="mt-4 p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
                  <h5 className="border-bottom pb-2 mb-4 fs-md fw-semibold">Map Excel Columns to Task Fields</h5>
                  <div className="row mb-2">
                    <div className="col-md-4">
                      <strong className="fs-sm">Field Name</strong>
                    </div>
                    <div className="col-md-4">
                      <strong className="fs-sm">Excel Column</strong>
                    </div>
                    <div className="col-md-4">
                      <strong className="fs-sm">Sample Value</strong>
                    </div>
                  </div>
                  {Object.entries(taskFields).map(([field, config]) => (
                    <div className="row mb-2 align-items-center" key={field}>
                      <div className="col-md-4">
                        <label className="fs-sm">{config.label} {config.required && (<span className="text-danger">*</span>)}</label>
                      </div>
                      <div className="col-md-4">
                        <select
                          className={`form-select bg-white shadow-none border-gray-300 fs-sm ${invalidFields.includes(field) ? "is-invalid" : ""}`}
                          value={mapping[field] || ""}
                          onChange={(e) => handleMappingChange(field, e.target.value)}
                        >
                          <option value="">Select column...</option>
                          {headers.map((header, idx) => (
                            <option key={idx} value={header}>{header}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <small className="fs-sm">{getSampleValue(field) || "—"}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {validationError && (
                <div className="alert alert-danger mt-4 d-flex align-items-center fw-semibold">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {validationError}
                </div>
              )}

              {(fileData && fileData.length > 0) || isLoading ? (
                <div className="card shadow-sm border-primary mt-4" style={{ maxWidth: 500 }}>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-2">
                      <i className="bi bi-bar-chart-fill text-primary fs-4 me-2"></i>
                      <h6 className="mb-0 fw-bold text-primary">Upload Report</h6>
                    </div>
                    <div className="row text-center">
                      <div className="col-4">
                        <div className="text-secondary fw-bold fs-5">
                          <i className="bi bi-collection me-1"></i>
                          {fileData && fileData.length ? fileData.length : 0}
                        </div>
                        <div className="small text-muted">Total Records</div>
                      </div>
                      <div className="col-4">
                        <div className="text-success fw-bold fs-5">
                          <i className="bi bi-plus-circle me-1"></i>
                          {uploadStats.created}
                        </div>
                        <div className="small text-muted">Created</div>
                      </div>
                      <div className="col-4">
                        <div className="text-danger fw-bold fs-5">
                          <i className="bi bi-x-circle me-1"></i>
                          {uploadStats.failed}
                        </div>
                        <div className="small text-muted">Failed</div>
                      </div>
                    </div>
                    {isLoading && (
                      <div className="mt-3 text-center">
                        <span className="spinner-border spinner-border-sm text-primary me-2"></span>
                        <span className="text-primary">Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="modal-footer d-flex justify-content-end pt-3 border-top mt-4">
            {uploadStats.totalProcessed > 0 && (
              <button
                type="button"
                className="btn btn-success d-flex align-items-center gap-2"
                data-bs-dismiss="modal"
                onClick={() => {
                  afterModalClose(uploadStats.records);
                  handleCloseModal();
                }}
              >
                <span className="material-symbols-outlined me-1">check_circle</span> Close & Reload Tasks
              </button>
            )}
            {Object.keys(mapping).length > 0 && (
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={handleSubmit}
                disabled={isLoading || !fileData}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">upload</span> Upload Tasks
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvCustomUploadModal;